import React from 'react';

import styled from 'styled-components';

import CellWrapper from '../cells/CellWrapper';
import { useMaterialsTableContext } from '../MaterialsTableContext';
import Icon from '@/ui/components/Icon';

export const CellParent = ({ cell, index, children }) => {
  const { activeColumn, handleActiveColumn } = useMaterialsTableContext();
  const { key, ...cellProps } = cell.getCellProps();

  return (
    <div
      key={key}
      {...cellProps}
      className={`td ${index === activeColumn ? 'active' : ''}${
        cell.isAggregated ? ' aggregated' : ''
      }`}
      onMouseEnter={() => index !== activeColumn && handleActiveColumn(index)}
    >
      {children}
    </div>
  );
};

const IconWrapper = styled.span`
  color: ${({ theme }) => theme.custom.colors.gray300};
  margin-left: -16px;
  width: 14px;
`;

const Cell = ({ cell, index, row }) => {
  /**
   * Grouped cell = row header cell
   */
  if (cell.isGrouped) {
    const iconName = row.isExpanded ? 'angle-down' : 'angle-right';
    cell.render('Aggregated');

    return (
      <CellParent cell={cell} index={index}>
        <CellWrapper {...row.getToggleRowExpandedProps()}>
          <IconWrapper>
            <Icon name={iconName} />
          </IconWrapper>
          {cell.render('GroupedCell')}
        </CellWrapper>
      </CellParent>
    );
  }

  if (cell.isAggregated) {
    return (
      <CellParent cell={cell} index={index}>
        {cell.render('Aggregated')}
      </CellParent>
    );
  }

  if (cell.isPlaceholder) {
    return (
      <CellParent cell={cell} index={index}>
        {cell.render('Placeholder')}
      </CellParent>
    );
  }

  return (
    <CellParent cell={cell} index={index}>
      {cell.render('Cell')}
    </CellParent>
  );
};

const Row = ({ row, prepareRow }) => {
  prepareRow(row);

  const { expandedRows, handleRowExpansion } = useMaterialsTableContext();

  const isSubRow = row.depth > 0;

  const isAnyRowExpanded = Object.entries(expandedRows).some(
    ([key, value]) => value && key !== row.id && !key.includes('.')
  );

  const className = ['tr']
    .concat(row.isExpanded ? 'expanded-row' : '')
    .concat(isSubRow ? 'child-row' : 'parent-row')
    .concat(
      !row.isExpanded && !isSubRow && isAnyRowExpanded ? 'gray-scale-row' : ''
    )
    .filter(Boolean)
    .join(' ');

  const { key, ...rowProps } = row.getRowProps();

  const onClick = () => {
    if (row.canExpand) {
      handleRowExpansion(row.id);
    }
  };

  return (
    <div key={key} {...rowProps} className={className} onClick={onClick}>
      {row.cells.map((cell, index) => (
        <Cell
          key={`${row.id}_${cell.column.id}_${index}`}
          cell={cell}
          index={index}
          row={row}
        />
      ))}
    </div>
  );
};

export default function Rows({ rows, getTableBodyProps, prepareRow }) {
  const { key, ...bodyProps } = getTableBodyProps();

  return (
    <div key={key} {...bodyProps} className="body">
      {rows.map(row => (
        <Row key={row.id} row={row} prepareRow={prepareRow} />
      ))}
    </div>
  );
}
